import { EPublisherSolutionModelSubType } from 'common/contracts';
import { IPermissions } from 'common/permissions.types';
import {
  ordersTabsLabelsMap,
  playersTabsLabelsMap,
  pricingTabsLabelsMap,
  reportsTabsLabelsMap,
  settingTabsLabelsMap,
  themeTabsLabelsMap
} from 'constants/navigation';
import {
  companyLevelNavItems,
  portalLevelNavItems,
  portalLevelStoreNavItems,
  projectLevelNavItems
} from 'design-system/AcSidebar/constants';
import { ESideBarContext } from 'design-system/AcSidebar/types';
import { UserDetails } from 'pages/Login/Login.types';
import { capitalizeFirstLetter } from 'utils/formattingUtil';
import { checkDefaultPageName } from 'utils/navigationUtil';
import { handleCouponsBreadcrumb } from './couponsBreadcrumb.util';

export interface BreadcrumbItem {
  label: string;
  path: string;
  isActive?: boolean;
  context?: ESideBarContext;
}

type PathSegment = {
  segment: string;
  index: number;
  isFirstOccurrence: boolean;
  isLast: boolean;
};

const contextByPath: Record<string, ESideBarContext> = {
  company: ESideBarContext.COMPANY,
  portal: ESideBarContext.PORTAL,
  project: ESideBarContext.PROJECT,
  store: ESideBarContext.PORTAL_LEVEL_STORE
};

const navItemsByContext = {
  [ESideBarContext.COMPANY]: companyLevelNavItems,
  [ESideBarContext.PORTAL]: portalLevelNavItems,
  [ESideBarContext.PORTAL_LEVEL_STORE]: portalLevelStoreNavItems,
  [ESideBarContext.PROJECT]: projectLevelNavItems
};

const getNameByContext = {
  [ESideBarContext.COMPANY]: ({ companyDisplayName }: UserDetails) =>
    companyDisplayName,
  [ESideBarContext.PORTAL]: ({ portals }: UserDetails, id?: string) =>
    portals?.find((p) => p.portalId === id)?.portalName,
  [ESideBarContext.PROJECT]: ({ projects }: UserDetails, id?: string) =>
    projects?.find((p) => p.publisherId === id)?.publisherName,
  [ESideBarContext.PORTAL_LEVEL_STORE]: (
    { portals }: UserDetails,
    id?: string
  ) => {
    const portal = portals?.find((p) =>
      p.stores?.some((s) => s.storeId === id)
    );
    return portal?.stores?.find((s) => s.storeId === id)?.storeName;
  }
};

export const getEntityName = (
  userDetails: UserDetails | null | undefined,
  context?: ESideBarContext,
  id?: string
): string | undefined => {
  if (!id || !userDetails || !context) return;
  const entityNameGetter = getNameByContext[context];
  return entityNameGetter(userDetails, id);
};

export const findNavItemByPath = (
  path: string,
  context: ESideBarContext
): { label: string; defaultTab?: string } | undefined => {
  const contextNavItems = navItemsByContext[context];
  for (const group of contextNavItems) {
    const item = group.items.find((item) => {
      const itemPath = item.url.split('/')[0];
      return itemPath === path;
    });
    if (item) {
      return {
        label: item.title,
        defaultTab: item?.defaultTab?.[context]
      };
    }
  }
  return undefined;
};

const createBreadcrumb = (
  label: string,
  path: string,
  context?: ESideBarContext,
  isActive?: boolean
): BreadcrumbItem => ({
  label,
  path,
  context,
  isActive
});

const getDefaultPath = (
  context: ESideBarContext,
  entityId?: string,
  permissions?: IPermissions
): string => {
  const defaultPage = checkDefaultPageName(context, permissions, entityId);
  return `/${context}/${entityId}/${defaultPage}`;
};

const getTabInfo = (
  segment: string,
  context: ESideBarContext
): { label: string; defaultTab?: string } => {
  const navItemInfo = findNavItemByPath(segment, context);
  if (navItemInfo) return navItemInfo;

  const tabLabelsMap = [
    settingTabsLabelsMap,
    themeTabsLabelsMap,
    pricingTabsLabelsMap,
    ordersTabsLabelsMap,
    reportsTabsLabelsMap,
    playersTabsLabelsMap
  ].find((map) => segment in map);

  if (!tabLabelsMap) return { label: capitalizeFirstLetter(segment) };

  return { label: tabLabelsMap[segment as keyof typeof tabLabelsMap] };
};

const getNestedBreadcrumbs = (
  pathSegments: string[],
  currentContext: ESideBarContext,
  basePath?: string
): BreadcrumbItem[] => {
  if (!pathSegments.length) return [];

  const prefix = basePath || '';
  const breadcrumbs: BreadcrumbItem[] = [];
  const seenPaths = new Set<string>();

  pathSegments.forEach((segment, index) => {
    const pathSegment: PathSegment = {
      segment,
      index,
      isFirstOccurrence: pathSegments.indexOf(segment) === index,
      isLast: index === pathSegments.length - 1
    };

    let { label, defaultTab } = getTabInfo(pathSegment.segment, currentContext);

    // Coupons-specific overrides
    const coupons = handleCouponsBreadcrumb({
      pathSegments,
      segment,
      index,
      isLast: pathSegment.isLast,
      prefix
    });
    if (coupons.skip) {
      return;
    }
    if (coupons.labelOverride) {
      label = coupons.labelOverride;
    }

    // Don't append defaultTab if it's the same as the segment to avoid duplicate paths like /orders/orders
    const shouldAppendDefaultTab = defaultTab && defaultTab !== segment;

    const base = `${prefix}/${segment}`;
    let path = shouldAppendDefaultTab ? `${base}/${defaultTab}` : base;
    if (coupons.pathOverride) {
      path = coupons.pathOverride;
    }

    // Only add breadcrumb if we haven't seen this path before
    if (!seenPaths.has(path)) {
      seenPaths.add(path);
      breadcrumbs.push(
        createBreadcrumb(label, path, currentContext, pathSegment.isLast)
      );
    }
  });

  return breadcrumbs;
};

const createContextLevelBreadcrumbMap = (
  entityName: string,
  entityId: string,
  userDetails: UserDetails,
  permissions: IPermissions,
  pathSegments: string[]
): Partial<Record<ESideBarContext, () => BreadcrumbItem[]>> => {
  const [, , ...nestedSegments] = pathSegments;
  return {
    [ESideBarContext.PROJECT]: () => [
      createBreadcrumb(
        entityName,
        getDefaultPath(ESideBarContext.PROJECT, entityId, permissions),
        ESideBarContext.PROJECT
      ),
      ...getNestedBreadcrumbs(
        nestedSegments,
        ESideBarContext.PROJECT,
        `/${ESideBarContext.PROJECT}/${entityId}`
      )
    ],
    [ESideBarContext.PORTAL]: () => [
      createBreadcrumb(
        entityName,
        getDefaultPath(ESideBarContext.PORTAL, entityId, permissions),
        ESideBarContext.PORTAL
      ),
      ...getNestedBreadcrumbs(
        nestedSegments,
        ESideBarContext.PORTAL,
        `/${ESideBarContext.PORTAL}/${entityId}`
      )
    ],
    [ESideBarContext.PORTAL_LEVEL_STORE]: () => {
      const portal = userDetails.portals?.find((p) =>
        p.stores?.some((s) => s.storeId === entityId)
      );
      if (!portal?.portalId) return [];

      return [
        createBreadcrumb(
          portal.portalName,
          getDefaultPath(ESideBarContext.PORTAL, portal.portalId, permissions),
          ESideBarContext.PORTAL
        ),
        createBreadcrumb(
          entityName,
          `/${ESideBarContext.PORTAL_LEVEL_STORE}/${entityId}/bundles`,
          ESideBarContext.PORTAL_LEVEL_STORE
        ),
        ...getNestedBreadcrumbs(
          nestedSegments,
          ESideBarContext.PORTAL_LEVEL_STORE,
          `/${ESideBarContext.PORTAL_LEVEL_STORE}/${entityId}`
        )
      ];
    }
  };
};

export const getBreadcrumbs = (
  pathname: string,
  params: Record<string, string | undefined>,
  userDetails: UserDetails | null | undefined,
  permissions: IPermissions
): BreadcrumbItem[] => {
  if (!userDetails) return [];

  const pathSegments = pathname.split('/').filter(Boolean);
  const isSingleProject = userDetails?.projects?.length === 1;
  const isPaymentLinkProject =
    userDetails?.projects?.[0]?.projectSubType ===
    EPublisherSolutionModelSubType.PAYMENT_LINKS;

  if (pathSegments.length === 0) return [];

  if (pathSegments[0] === 'welcome') return [];

  const currentContext =
    contextByPath[pathSegments[0]] || ESideBarContext.COMPANY;
  const entityId = params.portalId || params.publisherId;
  const entityName = getEntityName(userDetails, currentContext, entityId);

  const baseBreadcrumb = createBreadcrumb(
    userDetails?.companyDisplayName || userDetails?.company || '',
    '/welcome',
    ESideBarContext.COMPANY
  );

  if (currentContext === ESideBarContext.COMPANY) {
    const nestedBreadcrumbs = getNestedBreadcrumbs(
      pathSegments,
      ESideBarContext.COMPANY
    );
    return [baseBreadcrumb, ...nestedBreadcrumbs];
  }

  if (!entityId || !entityName) return [baseBreadcrumb];

  const contextLevelBreadcrumbs =
    createContextLevelBreadcrumbMap(
      entityName,
      entityId,
      userDetails,
      permissions,
      pathSegments
    )[currentContext]?.() || [];

  if (isSingleProject && isPaymentLinkProject) {
    return contextLevelBreadcrumbs;
  }

  return [baseBreadcrumb, ...contextLevelBreadcrumbs];
};
